import InfoIcon from "@/assets/icons/base64/InfoIcon"
import { appBorrowRepayPopupState } from "@/atoms/appPopupState"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { currentSlotAtom } from "@/atoms/pools"
import { SelectedReserveType } from "@/atoms/shared"
import { RelendTooltip } from "@/components/relendTooltip"
import { Stat } from "@/components/stat"
import { NFTDetailType } from "@/hooks/useFetchNFT"
import useTrans from "@/hooks/useTrans"
import { borrowConfigs } from "@/utils/actions/borrow"
import {
  calculateNewBorrowedAmountAndCompoundedInterestRate,
  formatToDecimals,
  repayConfigs,
} from "@/utils/actions/repay"
import {
  BORROW,
  ENABLE_FREE_BORROW_FEE,
  FREE_BORROW_FEE_ASSETS,
  LOCALE_EN,
  LOCALE_VI,
  REPAY,
  SKELETON_VALUE,
} from "@/utils/constants"
import { StyleObject, TokenSymbol, isStringInStringArray, tokenDecimalDigits } from "@/utils/helpers"
import { RplToken } from "@/utils/modals/RplToken"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import { Configuration, calculateBorrowInterest } from "@/utils/rate"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Box, Link, Stack, Typography } from "@mui/material"
import { RE_NGN, RE_VND, VND, calculateNewRatesAfterRefreshReserve } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import Image from "next/image"
import { useRouter } from "next/router"
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import * as styles from "./styles"
import { NFTVoucherType, SelectedNFTType } from "./view"

type BorrowRepayModalStatProps = {
  selectedReserve: SelectedReserveType
  value: number | undefined
  type: typeof BORROW | typeof REPAY
  selectedNFT?: SelectedNFTType
  handleUpdateNFTInterestAmount: (voucherType: NFTVoucherType, value: number) => void
}

type TitleKeys =
  | "borrowAPR"
  | "borrowFee"
  | "borrowedAmount"
  | "totalRepayAmount"
  | "interestToPay"
  | "borrowUltilization"
  | "yourTotalBorrow"
  | "yourTotalBorrowLimit"
  | "liquidationThreshold"

type TitleStat = {
  [key in TitleKeys]: React.ReactNode
}

type StatPropsWithKey = {
  titleKey: TitleKeys
  content: React.ReactNode
  contentStyle?: StyleObject
  statsWrapper?: StyleObject
  statsTitleWrapper?: StyleObject
}

const calculateDiscountedAmount = (
  baseAmount: BigNumber,
  selectedNFT: NFTDetailType | undefined,
  selectedReserve: SelectedReserveType,
  isFiatToken: boolean,
): [number, number, ReactNode, ReactNode] => {
  const roundingStrategy = isFiatToken ? BigNumber.ROUND_UP : undefined

  let discountedAmount = -1
  if (selectedNFT) {
    const discountPercentage = selectedNFT.detail?.discountPercentage || BigNumber(0)
    const discountedValue = baseAmount.multipliedBy(discountPercentage.div(100))

    const maximumDiscountedAmountInToken = new BigNumber(selectedNFT.detail?.maximumAmount ?? 0)
      .div(selectedReserve.maxPrice)
      .decimalPlaces(selectedReserve.decimals, roundingStrategy)

    discountedAmount = baseAmount
      .minus(BigNumber.min(discountedValue, maximumDiscountedAmountInToken))
      .decimalPlaces(selectedReserve.decimals, roundingStrategy)
      .toNumber()
  }

  const formattedBaseAmount = formatToken(
    baseAmount,
    tokenDecimalDigits[selectedReserve.symbol as TokenSymbol],
    false,
    false,
    isFiatToken ? BigNumber.ROUND_UP : undefined,
    undefined,
    styles.semiBoldTextEnd,
  )

  const formattedDiscountedAmount = formatToken(
    discountedAmount,
    tokenDecimalDigits[selectedReserve.symbol as TokenSymbol],
    false,
    false,
    isFiatToken ? BigNumber.ROUND_UP : undefined,
    undefined,
    styles.semiBoldTextEnd,
  )

  return [baseAmount.toNumber(), discountedAmount, formattedBaseAmount, formattedDiscountedAmount]
}

const BorrowRepayModalStat: FC<BorrowRepayModalStatProps> = ({
  selectedReserve,
  value,
  type,
  selectedNFT,
  handleUpdateNFTInterestAmount,
}) => {
  const trans = useTrans()
  const router = useRouter()
  const locale = router.locale === LOCALE_VI ? LOCALE_VI : LOCALE_EN
  const [showMore, setShowMore] = useState(false)
  const currentSlot = useAtomValue(currentSlotAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const popupState = useAtomValue(appBorrowRepayPopupState)

  // configs
  const valueNonNan = value?.toString() ?? "0"
  const isBorrow = type === BORROW
  const borrowCalculationStats = borrowConfigs.getNewCalculations(selectedObligation, selectedReserve, valueNonNan)
  const repayCalculationStats = repayConfigs.getNewCalculations(selectedObligation, selectedReserve, valueNonNan)

  const utilization = isBorrow
    ? borrowCalculationStats.utilization ?? BigNumber(0)
    : repayCalculationStats.utilization ?? BigNumber(0)
  const newBorrowUtilization = isBorrow
    ? borrowCalculationStats.newBorrowUtilization
    : repayCalculationStats.newBorrowUtilization

  const newUtilizationDisplay = useMemo(() => {
    if (!newBorrowUtilization) return null

    if (newBorrowUtilization.isGreaterThanOrEqualTo(1)) {
      return formatPercent(1)
    } else if (newBorrowUtilization.isLessThan(0)) {
      return formatPercent(0)
    } else {
      return formatPercent(newBorrowUtilization)
    }
  }, [newBorrowUtilization])

  const borrowLimit = useMemo(
    () =>
      isBorrow ? formatUsd(borrowCalculationStats.borrowLimit ?? 0) : formatUsd(repayCalculationStats.borrowLimit ?? 0),
    [isBorrow, borrowCalculationStats.borrowLimit, repayCalculationStats.borrowLimit],
  )

  // config for new borrowAPR calculation
  const newBorrowedAmount = useMemo(
    () =>
      popupState.type === BORROW
        ? selectedReserve.totalBorrow.plus(value ?? 0)
        : selectedReserve.totalBorrow.minus(value ?? 0),
    [popupState.type, selectedReserve.totalBorrow, value],
  )

  const newAvailableAmount = useMemo(
    () =>
      popupState.type === BORROW
        ? selectedReserve.availableAmount.minus(value ?? 0)
        : selectedReserve.availableAmount.plus(value ?? 0),
    [popupState.type, selectedReserve.availableAmount, value],
  )

  const config: Configuration = useMemo(
    () => ({
      protocolTakeRate: selectedReserve.interestRateSpread,
      isShowingAPY: false,
      borrowedAmount: newBorrowedAmount.isGreaterThanOrEqualTo(0) ? newBorrowedAmount : BigNumber(0.0000000001),
      availableAmount: newAvailableAmount.isGreaterThanOrEqualTo(0) ? newAvailableAmount : BigNumber(0.0000000001),
      optimalUtilizationRate: selectedReserve.targetUtilization,
      maxUtilizationRate: selectedReserve.maxUtilizationRate,
      minBorrowRate: selectedReserve.minBorrowApr,
      optimalBorrowRate: selectedReserve.targetBorrowApr,
      maxBorrowRate: selectedReserve.maxBorrowApr,
      superMaxBorrowRate: selectedReserve.superMaxBorrowRate,
      accumulatedProtocolFees: selectedReserve.accumulatedProtocolFeesWad,
      decimals: selectedReserve.decimals,
    }),
    [selectedReserve, newBorrowedAmount, newAvailableAmount],
  )

  const borrowObligation = selectedObligation?.borrows.find((b) => selectedReserve.symbol === b.symbol)
  const { newCummulativeBorrowRate: newReserveCummulativeBorrowRate } = calculateNewRatesAfterRefreshReserve(
    selectedReserve,
    currentSlot,
  )

  const isFiatToken = useMemo(
    () => selectedReserve.symbol === RE_VND || selectedReserve.symbol === RE_NGN || selectedReserve.symbol === VND,
    [selectedReserve],
  )

  const selectedNFTKey = type === BORROW ? NFTVoucherType.BORROW : NFTVoucherType.REPAY

  const [, borrowFeeDiscountedAmount, formattedBorrowFee, formattedBorrowFeeDiscountedAmount] = useMemo(() => {
    const baseBorrowFee = isBorrow
      ? borrowCalculationStats.calculatedBorrowFee ?? BigNumber(0)
      : repayCalculationStats.calculatedBorrowFee ?? BigNumber(0)

    return calculateDiscountedAmount(baseBorrowFee, selectedNFT?.[selectedNFTKey], selectedReserve, isFiatToken)
  }, [
    isBorrow,
    borrowCalculationStats,
    repayCalculationStats,
    selectedNFT,
    selectedReserve,
    isFiatToken,
    selectedNFTKey,
  ])

  const [interestAmount, interestDiscountedAmount, formattedInterestAmount, formattedInterestDiscountedAmount] =
    useMemo(() => {
      if (!newReserveCummulativeBorrowRate || !borrowObligation?.cumulativeBorrowRate) return [0, -1, "0", "-1"]

      const valInterestAmount = newReserveCummulativeBorrowRate
        .dividedBy(borrowObligation?.cumulativeBorrowRate)
        .minus(1)
        .multipliedBy(borrowObligation?.borrowedAmountDecimals)
        .abs()
        .decimalPlaces(selectedReserve.decimals, isFiatToken ? BigNumber.ROUND_UP : undefined)

      // TODO: update when script is done
      // const valInterestAmount = newBorrowedAmountAfterRefresh
      //   .minus(borrowObligation.principalBorrowedAmount)
      //   .abs()
      //   .decimalPlaces(selectedReserve.decimals, isFiatToken ? BigNumber.ROUND_UP : undefined)

      return calculateDiscountedAmount(valInterestAmount, selectedNFT?.[selectedNFTKey], selectedReserve, isFiatToken)
    }, [newReserveCummulativeBorrowRate, borrowObligation, selectedReserve, isFiatToken, selectedNFT, selectedNFTKey])

  let repayMax = BigNumber(0)
  if (selectedReserve && borrowObligation && currentSlot) {
    const { newBorrowedAmountAfterRefresh } = calculateNewBorrowedAmountAndCompoundedInterestRate(
      selectedReserve,
      borrowObligation,
      currentSlot,
    )
    repayMax = formatToDecimals(newBorrowedAmountAfterRefresh, selectedReserve.decimals)
  }

  const borrowedAmount = borrowObligation?.amount ?? new BigNumber(0)
  const totalTokenBorrowed = borrowedAmount.isLessThan(repayMax) ? repayMax : borrowedAmount
  const borrowedAmountWithoutInterest = totalTokenBorrowed.minus(interestAmount)

  const totalBorrowValue = useMemo(() => {
    if (!selectedObligation) return new BigNumber(0)

    let weightedTotalBorrowValue = selectedObligation.weightedTotalBorrowValue
    const inputedAmount = value || 0
    const price = selectedReserve?.price ?? new BigNumber(0)
    const inputedAmountToPriceUsd = price.multipliedBy(inputedAmount)

    if (type === BORROW) weightedTotalBorrowValue = weightedTotalBorrowValue.plus(inputedAmountToPriceUsd)
    if (type === REPAY) weightedTotalBorrowValue = weightedTotalBorrowValue.minus(inputedAmountToPriceUsd)

    if (weightedTotalBorrowValue.lte(0)) return new BigNumber(0)

    return weightedTotalBorrowValue
  }, [type, value, selectedObligation, selectedReserve])

  const tokenDisplayName = RplToken.getTokenDisplayName(selectedReserve.symbol, selectedReserve.name ?? "")

  const titleEls: TitleStat = useMemo(() => {
    // isSymbolFreeBorrowFee is true if free borrow fee mode is enabled and applies to all assets (when FREE_BORROW_FEE_ASSETS is empty) or just the selected asset (when its symbol is in FREE_BORROW_FEE_ASSETS).
    const isSymbolFreeBorrowFee =
      ENABLE_FREE_BORROW_FEE &&
      (FREE_BORROW_FEE_ASSETS.length === 0 ||
        isStringInStringArray(selectedReserve.symbol as TokenSymbol, FREE_BORROW_FEE_ASSETS))

    return {
      borrowAPR: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.borrow_apr}</Typography>
          <RelendTooltip title={trans.dashboard.modal.borrow_apr_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      borrowFee:
        type === BORROW ? (
          <>
            <Box>
              <Typography className='text-neutral-1000'>
                {trans.dashboard.modal.borrow_fee}{" "}
                {ENABLE_FREE_BORROW_FEE || isSymbolFreeBorrowFee ? (
                  <Typography component='span' sx={styles.blackFridayText} className='text-neutral-1000'>
                    {trans.dashboard.modal.free_borrow_fee}
                  </Typography>
                ) : null}
              </Typography>
            </Box>
            <RelendTooltip title={trans.dashboard.modal.borrow_fee_tooltip}>
              <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
            </RelendTooltip>
          </>
        ) : null,
      borrowedAmount: (
        <Typography className='text-neutral-1000'>
          {locale === LOCALE_VI ? trans.dashboard.modal.so : null} {tokenDisplayName} {trans.dashboard.modal.borrowed}
        </Typography>
      ),
      interestToPay: <Typography className='text-neutral-1000'>{trans.dashboard.modal.interest_to_pay}</Typography>,
      borrowUltilization: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.borrow_utilization}</Typography>
          <RelendTooltip title={trans.dashboard.modal.borrow_utilization_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      yourTotalBorrow: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.your_total_borrow}</Typography>
          <RelendTooltip title={trans.dashboard.modal.your_total_borrow_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      yourTotalBorrowLimit: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.your_total_borrow_limit}</Typography>
          <RelendTooltip title={trans.dashboard.modal.total_borrow_limit_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      liquidationThreshold: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.liquidation_threshold}</Typography>
          <RelendTooltip title={trans.dashboard.modal.liquidation_threshold_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      totalRepayAmount:
        type === REPAY ? (
          <Typography className='text-neutral-1000'>
            {trans.dashboard.modal.total_amount_repay(tokenDisplayName)}
          </Typography>
        ) : null,
    }
  }, [trans, locale, type, selectedReserve, tokenDisplayName])

  const stats: StatPropsWithKey[] = useMemo(
    () => [
      {
        titleKey: "borrowAPR",
        content: (
          <Box sx={styles.borrowAPYText}>
            {formatPercent(selectedReserve.borrowAPRInterest)}
            {value ? <>&rarr; {formatPercent(calculateBorrowInterest(config))}</> : null}
          </Box>
        ),
      },
      {
        titleKey: "borrowFee",
        content:
          type === BORROW ? (
            <>
              {selectedObligation && (
                <>
                  {borrowFeeDiscountedAmount >= 0 ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 4 }}>
                      <span style={{ color: "#D81C1C", textDecorationLine: "line-through" }}>{formattedBorrowFee}</span>
                      <span style={{ color: "#099C0F", fontWeight: 600 }}>{formattedBorrowFeeDiscountedAmount}</span>
                    </div>
                  ) : (
                    <>{formattedBorrowFee}</>
                  )}{" "}
                  {tokenDisplayName}
                </>
              )}
              {!selectedObligation && SKELETON_VALUE}
            </>
          ) : null,
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "interestToPay",
        content: (
          <>
            {selectedObligation && (
              <>
                {type === BORROW ? (
                  formattedInterestAmount
                ) : (
                  <>
                    {interestDiscountedAmount >= 0 ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "end", gap: 4 }}>
                        <span style={{ color: "#D81C1C", textDecorationLine: "line-through" }}>
                          {formattedInterestAmount}
                        </span>
                        <span style={{ color: "#099C0F", fontWeight: 600 }}>{formattedInterestDiscountedAmount}</span>
                      </div>
                    ) : (
                      <>{formattedInterestAmount}</>
                    )}
                  </>
                )}{" "}
                {tokenDisplayName}
              </>
            )}
            {!selectedObligation && SKELETON_VALUE}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "borrowedAmount",
        content: (
          <>
            {selectedObligation && (
              <>
                {formatToken(borrowedAmountWithoutInterest, tokenDecimalDigits[selectedReserve.symbol as TokenSymbol])}{" "}
                {tokenDisplayName}
              </>
            )}
            {!selectedObligation && SKELETON_VALUE}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "totalRepayAmount",
        content:
          type === REPAY ? (
            <>
              {selectedObligation && (
                <>
                  {formatToken(totalTokenBorrowed, tokenDecimalDigits[selectedReserve.symbol as TokenSymbol])}{" "}
                  {tokenDisplayName}
                </>
              )}
              {!selectedObligation && SKELETON_VALUE}
            </>
          ) : null,
        contentStyle: styles.semiBoldTextEnd,
      },
    ],
    [
      selectedReserve,
      value,
      config,
      type,
      selectedObligation,
      borrowFeeDiscountedAmount,
      formattedBorrowFee,
      formattedBorrowFeeDiscountedAmount,
      tokenDisplayName,
      formattedInterestAmount,
      interestDiscountedAmount,
      formattedInterestDiscountedAmount,
      borrowedAmountWithoutInterest,
      totalTokenBorrowed,
    ],
  )

  const statsMore: StatPropsWithKey[] = useMemo(
    () => [
      {
        titleKey: "borrowUltilization",
        content: (
          <>
            {selectedObligation && (
              <>
                {formatPercent(utilization)}
                {Boolean(value) && newBorrowUtilization && (
                  <>
                    {" \u2192 "}
                    {newUtilizationDisplay}
                  </>
                )}
              </>
            )}
            {!selectedObligation && SKELETON_VALUE}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "yourTotalBorrow",
        content: (
          <>
            {selectedObligation && <>{formatUsd(totalBorrowValue)}</>}
            {!selectedObligation && SKELETON_VALUE}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "yourTotalBorrowLimit",
        content: <>{borrowLimit}</>,
        contentStyle: styles.borrowLimitText,
      },
      {
        titleKey: "liquidationThreshold",
        content: selectedObligation
          ? `${formatUsd(selectedObligation.liquidationThreshold.toString())}`
          : SKELETON_VALUE,
        contentStyle: styles.liquidationThresholdText,
      },
    ],
    [
      borrowLimit,
      selectedObligation,
      utilization,
      newBorrowUtilization,
      newUtilizationDisplay,
      value,
      totalBorrowValue,
    ],
  )

  useEffect(() => {
    handleUpdateNFTInterestAmount(selectedNFTKey, interestDiscountedAmount)
  }, [handleUpdateNFTInterestAmount, interestDiscountedAmount, selectedNFTKey])

  const handleShowMore = useCallback(() => {
    setShowMore((prev) => !prev)
  }, [])

  return (
    <Stack spacing={0} mb={3}>
      {stats.map((stat, index) => (
        <Stat
          key={`${stat.titleKey}-${index}`}
          titleEl={titleEls[stat.titleKey]}
          content={stat.content}
          contentStyle={stat.contentStyle}
        />
      ))}

      <Box className='border-t-2 border-t-secondary border-dashed my-2' />

      {showMore &&
        statsMore.map((stat, index) => (
          <Stat
            key={`${stat.titleKey}-${index}`}
            titleEl={titleEls[stat.titleKey]}
            content={stat.content}
            contentStyle={stat.contentStyle}
          />
        ))}

      <Link
        onClick={handleShowMore}
        data-testid='btn-show-more-less'
        className='text-primary text-xs sm:text-sm m-auto cursor-pointer hover:text-primary/80 mt-1'
      >
        {showMore ? trans.dashboard.modal.show_less : trans.dashboard.modal.show_more}
        {showMore ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
      </Link>
    </Stack>
  )
}

export default BorrowRepayModalStat
